import { createScopedReadCache } from './scoped-read-cache';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';
import { ApiClient } from './client';
import type { ApiResponse } from './types';
import type {
  CreateInboxCampaignPayload,
  InboxCampaign,
  InboxCampaignListResponse,
  InboxClaimResponse,
  InboxMessagesResponse,
  InboxTargetPreview,
  InboxTargetRule,
  UpdateInboxCampaignPayload,
} from '@/types/inbox';

class InboxApi extends ApiClient {
  async listMessages(params?: { limit?: number; offset?: number }): Promise<ApiResponse<InboxMessagesResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request(`/inbox/messages${suffix ? `?${suffix}` : ''}`);
  }

  async markRead(messageId: string): Promise<ApiResponse<{ updated: boolean; message_id: string }>> {
    sidebarInboxCache.clear();
    return this.request(`/inbox/messages/${encodeURIComponent(messageId)}/read`, { method: 'POST' });
  }

  async markAllRead(): Promise<ApiResponse<{ updated_count: number }>> {
    sidebarInboxCache.clear();
    return this.request('/inbox/messages/read-all', { method: 'POST' });
  }

  async claim(messageId: string): Promise<ApiResponse<InboxClaimResponse>> {
    sidebarInboxCache.clear();
    return this.request(`/inbox/messages/${encodeURIComponent(messageId)}/claim`, { method: 'POST' });
  }
}

class AdminInboxApi extends ApiClient {
  async listCampaigns(params?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<InboxCampaignListResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request(`/admin/inbox/campaigns${suffix ? `?${suffix}` : ''}`);
  }

  async previewTargets(targetRule: InboxTargetRule): Promise<ApiResponse<InboxTargetPreview>> {
    return this.request('/admin/inbox/campaigns/preview-targets', {
      method: 'POST',
      body: JSON.stringify({ target_rule: targetRule }),
    });
  }

  async createCampaign(payload: CreateInboxCampaignPayload): Promise<ApiResponse<InboxCampaign>> {
    return this.request('/admin/inbox/campaigns', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async publishCampaign(campaignId: string): Promise<ApiResponse<InboxCampaign>> {
    return this.request(`/admin/inbox/campaigns/${encodeURIComponent(campaignId)}/publish`, { method: 'POST' });
  }

  async updateCampaign(campaignId: string, payload: UpdateInboxCampaignPayload): Promise<ApiResponse<InboxCampaign>> {
    return this.request(`/admin/inbox/campaigns/${encodeURIComponent(campaignId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }
}

export const inboxApi = new InboxApi();
export const adminInboxApi = new AdminInboxApi();

// Read-only badge snapshots survive route remounts, but never account changes or mutations.
const sidebarInboxCache = createScopedReadCache<ApiResponse<InboxMessagesResponse>>(30_000);
registerAuthScopedCacheReset('sidebar-inbox', () => sidebarInboxCache.clear());
export function readSidebarInbox(force = false): Promise<ApiResponse<InboxMessagesResponse>> {
  if (force) sidebarInboxCache.clear();
  return sidebarInboxCache.read(getAuthScopeRevision(), async () => {
    const response = await inboxApi.listMessages({ limit: 20 });
    if (!response.success || !response.data) throw response.error ?? new Error('inbox_load_failed');
    return response;
  });
}
